import { useState, useEffect, useRef } from "react";

// react-router components
import { useLocation, Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import { jwtDecode } from "jwt-decode";
import styled from "styled-components";
import {
  APP_BASE_ROUTE,
  APP_DASHBOARD_ROUTE,
  APP_SETTINGS_ROUTE,
  LOGOUT_ROUTE,
  isAppRoute,
} from "utils/routes";

function DashboardNavbar({ absolute, light, isMini, actions }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const isInsideApp = isAppRoute(location.pathname);
  const route = isInsideApp ? pathSegments.slice(1) : pathSegments;
  const [userName, setUserName] = useState('');
  const [userImage, setUserImage] = useState('');
  const transparentNavbarRef = useRef(transparentNavbar);

  useEffect(() => {
    transparentNavbarRef.current = transparentNavbar;
  }, [transparentNavbar]);

  useEffect(() => {
    const token = localStorage.getItem('tradez-token');

    setUserImage(jwtDecode(token)?.image);
    setUserName(jwtDecode(token)['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']);
  }, [])

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      const nextTransparentNavbar = (fixedNavbar && window.scrollY === 0) || !fixedNavbar;

      if (transparentNavbarRef.current !== nextTransparentNavbar) {
        transparentNavbarRef.current = nextTransparentNavbar;
        setTransparentNavbar(dispatch, nextTransparentNavbar);
      }
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const handleOpenConfigCustom = () => {
    handleConfiguratorOpen();
    handleCloseMenu();
  };

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem onClick={handleOpenConfigCustom} icon={<Icon>settings</Icon>} title="Configurações" />
  
      <Link to={LOGOUT_ROUTE}>
        <NotificationItem icon={<Icon>logout</Icon>} title="Sair" />
      </Link>
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs
            icon="home"
            title={route[route.length - 1] || "Dashboard"}
            route={route}
            light={light}
            rootRoute={isInsideApp ? APP_DASHBOARD_ROUTE : "/"}
            basePath={isInsideApp ? APP_BASE_ROUTE : ""}
          />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"} display="flex" alignItems="center" gap={1}>
              {actions}
              <IconButton sx={navbarIconButton} size="small" disableRipple>
                {userImage
                  ? <StyledImg src={userImage} height={30} onClick={handleOpenMenu} />
                  : <Icon onClick={handleOpenMenu} sx={iconsStyle}>account_circle</Icon>
                }
              </IconButton>
              <IconButton
                className="menuHamburger"
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              {/* <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Icon sx={iconsStyle}>notifications</Icon>
              </IconButton> */}
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

const StyledImg = styled.img`

border-radius: 50px;
`;

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  actions: null,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  actions: PropTypes.node,
};

export default DashboardNavbar;
